<template>
  <teleport to="body">
    <WdsModal :show="open" :title="$t('Hire status overview')" @cancel="emit('close')">
      <WdsStack direction-column gap-md>
        <WdsText>
          <div class="tw-font-semibold">
            {{ $t('Hires can have the following statuses:') }}
          </div>
        </WdsText>

        <WdsStack direction-column gap-md>
          <!-- Draft -->
          <template v-if="isCompany">
            <WdsStack align-center justify-center gap-md>
              <div class="tw-w-24 tw-flex-shrink-0 tw-flex">
                <WdsLabel sm class="tw-bg-gray-100 tw-text-gray-600">
                  <WdsStack align-center gap-xs>
                    <div class="tw-w-1.5 tw-h-1.5 tw-bg-white tw-rounded-full"></div>
                    <span>{{ $t('Draft') }}</span>
                  </WdsStack>
                </WdsLabel>
              </div>
              <WdsText class="tw-flex-1">
                <div class="tw-font-normal">
                  {{
                    $t(
                      'Hire is in preparation and not yet shared with the worker. This may include pending approvals, onboarding, or classification steps.',
                    )
                  }}
                </div>
              </WdsText>
            </WdsStack>

            <WdsDivider />
          </template>

          <!-- Offered -->
          <WdsStack align-center justify-center gap-md>
            <div class="tw-w-24 tw-flex-shrink-0 tw-flex">
              <WdsLabel sm primary subtle>
                <WdsStack align-center gap-xs>
                  <div class="tw-w-1.5 tw-h-1.5 tw-bg-indigo-500 tw-rounded-full"></div>
                  <span>{{ $t('Offered') }}</span>
                </WdsStack>
              </WdsLabel>
            </div>
            <WdsText class="tw-flex-1">
              <div class="tw-font-normal">
                {{
                  $t(
                    "Offer has been sent and is visible to the worker. They haven't signed the contract yet and may need to complete required onboarding steps first. Conversation is open.",
                  )
                }}
              </div>
            </WdsText>
          </WdsStack>

          <WdsDivider />

          <!-- Ready -->
          <WdsStack align-center justify-center gap-md>
            <div class="tw-w-24 tw-flex-shrink-0 tw-flex">
              <WdsLabel sm success subtle>
                <WdsStack align-center gap-xs>
                  <div class="tw-w-1.5 tw-h-1.5 tw-bg-green-600 tw-rounded-full"></div>
                  <span>{{ $t('Ready') }}</span>
                </WdsStack>
              </WdsLabel>
            </div>
            <WdsText class="tw-flex-1">
              <div class="tw-font-normal">
                {{
                  $t(
                    'Contract is signed. Worker is ready to begin work on the contract start date.',
                  )
                }}
              </div>
            </WdsText>
          </WdsStack>

          <WdsDivider />

          <!-- Active -->
          <WdsStack align-center justify-center gap-md>
            <div class="tw-w-24 tw-flex-shrink-0 tw-flex">
              <WdsLabel sm success>
                <WdsStack align-center gap-xs>
                  <div class="tw-w-1.5 tw-h-1.5 tw-bg-white tw-rounded-full"></div>
                  <span>{{ $t('Active') }}</span>
                </WdsStack>
              </WdsLabel>
            </div>
            <WdsText class="tw-flex-1">
              <div class="tw-font-normal">
                {{ $t('The worker is currently working under contract.') }}
              </div>
            </WdsText>
          </WdsStack>

          <WdsDivider />

          <!-- Terminated -->
          <WdsStack align-center justify-center gap-md>
            <div class="tw-w-24 tw-flex-shrink-0 tw-flex">
              <WdsLabel sm error subtle>
                <WdsStack align-center gap-xs>
                  <div class="tw-w-1.5 tw-h-1.5 tw-bg-red-500 tw-rounded-full"></div>
                  <span>{{ $t('Terminated') }}</span>
                </WdsStack>
              </WdsLabel>
            </div>
            <WdsText class="tw-flex-1">
              <div class="tw-font-normal">
                {{ $t('Contract ended early by the Hiring Manager before the original end date.') }}
              </div>
            </WdsText>
          </WdsStack>

          <WdsDivider />

          <!-- Ended -->
          <WdsStack align-center justify-center gap-md>
            <div class="tw-w-24 tw-flex-shrink-0 tw-flex">
              <WdsLabel sm error subtle>
                <WdsStack align-center gap-xs>
                  <div class="tw-w-1.5 tw-h-1.5 tw-bg-red-500 tw-rounded-full"></div>
                  <span>{{ $t('Ended') }}</span>
                </WdsStack>
              </WdsLabel>
            </div>
            <WdsText class="tw-flex-1">
              <div class="tw-font-normal">
                {{
                  $t(
                    'Contract ended as scheduled. Work is complete. Payment requests may still be submitted.',
                  )
                }}
              </div>
            </WdsText>
          </WdsStack>

          <!-- Cancelled -->
          <template v-if="isCompany">
            <WdsDivider />
            <WdsStack align-center justify-center gap-md>
              <div class="tw-w-24 tw-flex-shrink-0 tw-flex">
                <WdsLabel sm subtle>
                  <WdsStack align-center gap-xs>
                    <div class="tw-w-1.5 tw-h-1.5 tw-bg-gray-700 tw-rounded-full"></div>
                    <span>{{ $t('Cancelled') }}</span>
                  </WdsStack>
                </WdsLabel>
              </div>
              <WdsText class="tw-flex-1">
                <div class="tw-font-normal">
                  {{
                    $t(
                      'Hire was cancelled before work began. All associated data is permanently deleted.',
                    )
                  }}
                </div>
              </WdsText>
            </WdsStack>
          </template>
        </WdsStack>
      </WdsStack>

      <template #footer>
        <WdsStack justify-space-between align-end gap-sm class="tw-w-full">
          <WdsStack>
            <WdsStack direction-column>
              <WdsText class="tw-pl-1">{{ $t('Need more help?') }}</WdsText>
              <WdsButtonGhost secondary href="https://help.worksome.com" target="_blank">
                <WdsStack align-center gap-sm>
                  <span>{{ $t("Visit Worksome's help center") }}</span>
                  <WdsIcon name="arrow-up-right" :size="16" />
                </WdsStack>
              </WdsButtonGhost>
            </WdsStack>
          </WdsStack>

          <WdsStack>
            <WdsButton @click="emit('close')">
              {{ $t('Close') }}
            </WdsButton>
          </WdsStack>
        </WdsStack>
      </template>
    </WdsModal>
  </teleport>
</template>

<script setup lang="ts">
import {
  WdsButton,
  WdsButtonGhost,
  WdsDivider,
  WdsIcon,
  WdsLabel,
  WdsModal,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'

import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'

const { isCompany } = useAccountCapabilities()

withDefaults(
  defineProps<{
    open?: boolean
  }>(),
  {
    open: false,
  },
)

const emit = defineEmits(['close'])
</script>
